import DiffMatchPatch from 'diff-match-patch'

function escapeHtmlString(str: string): string {
  return str.replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/**
 * Creates a screen reader only span with a description of the change
 */
function createScreenReaderText(
  type: 'deleted' | 'added',
  text: string,
): string {
  const description = type === 'deleted' ? 'Deleted:' : 'Added:'
  return `<span class="screen-reader-only">${description} </span>${escapeHtmlString(text)}`
}

function textDiff(
  previous: string,
  next: string,
  config?: {
    useEmptyString?: boolean
    emptyStringPlaceholder?: string
    compare?: boolean
  },
): string {
  const dmp = new DiffMatchPatch()
  const diffs = dmp.diff_main(previous || '', next || '')
  dmp.diff_cleanupSemantic(diffs)

  const options = {
    useEmptyString: true,
    emptyStringPlaceholder: ' ',
    compare: true,
    ...config,
  }

  const addedClass = options.compare ? ' diff-text--added' : ''
  const removedClass = options.compare ? ' diff-text--removed' : ''

  if (options?.useEmptyString) {
    if (!previous) {
      return `<del role="deletion" class="diff-text${removedClass}">${createScreenReaderText('deleted', options.emptyStringPlaceholder)}</del><ins role="insertion" class="diff-text${addedClass}">${createScreenReaderText('added', next)}</ins>`
    } else if (!next) {
      if (options.compare) {
        return `<del role="deletion" class="diff-text${removedClass}">${createScreenReaderText('deleted', previous)}</del><ins role="insertion" class="diff-text${addedClass}">${createScreenReaderText('added', options.emptyStringPlaceholder)}</ins>`
      } else {
        return `<del role="deletion" class="diff-text${removedClass}">${createScreenReaderText('deleted', options.emptyStringPlaceholder)}</del>`
      }
    }
  }

  let changedText = ''

  for (const [operation, text] of diffs) {
    if (operation === 0) {
      changedText += text
    } else if (operation === -1) {
      if (options.compare) {
        changedText += `<del role="deletion" class="diff-text${removedClass}">${createScreenReaderText('deleted', text)}</del>`
      } else {
        changedText += `<del role="deletion" class="diff-text${removedClass}">${createScreenReaderText('deleted', ' ')}</del>`
      }
    } else {
      changedText += `<ins role="insertion" class="diff-text${addedClass}">${createScreenReaderText('added', text)}</ins>`
    }
  }

  return changedText
}

function textDiffToPills(
  previous: string,
  next: string,
  config?: {
    useEmptyString?: boolean
    emptyStringPlaceholder?: string
    compare?: boolean
  },
): {
  deletionHtml: string
  insertionHtml: string
} {
  const dmp = new DiffMatchPatch()
  const diffs = dmp.diff_main(previous, next)
  dmp.diff_cleanupSemantic(diffs)

  const options = {
    useEmptyString: true,
    emptyStringPlaceholder: ' ',
    compare: true,
    ...config,
  }

  if (options?.useEmptyString) {
    if (!previous) {
      return {
        deletionHtml: createScreenReaderText(
          'deleted',
          options.emptyStringPlaceholder,
        ),
        insertionHtml: createScreenReaderText('added', next),
      }
    }
    if (!next) {
      return {
        deletionHtml: createScreenReaderText('deleted', previous),
        insertionHtml: options.compare
          ? createScreenReaderText('added', options.emptyStringPlaceholder)
          : '',
      }
    }
  }

  let deletionHtml = ''
  let insertionHtml = ''

  for (const [operation, text] of diffs) {
    const escaped = escapeHtmlString(text)

    if (operation === 0) {
      deletionHtml += escaped
      insertionHtml += escaped
    } else if (operation === -1) {
      deletionHtml += `<span role="deletion">${createScreenReaderText('deleted', text)}</span>`
    } else {
      insertionHtml += `<span role="insertion">${createScreenReaderText('added', text)}</span>`
    }
  }

  return { deletionHtml, insertionHtml }
}

export { textDiff, textDiffToPills, escapeHtmlString, createScreenReaderText }
